import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Book, ArrowLeft, ExternalLink, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { API } from "@/App";
import axios from "axios";

const ReadingPage = () => {
  const navigate = useNavigate();
  const [readings, setReadings] = useState([]);
  const [dailyReading, setDailyReading] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    fetchReadings();
  }, []);

  const fetchReadings = async () => {
    try {
      const [readingsRes, dailyRes] = await Promise.all([
        axios.get(`${API}/content/reading`),
        axios.get(`${API}/content/daily`)
      ]);
      setReadings(readingsRes.data.readings);
      setDailyReading(dailyRes.data.reading);
    } catch (error) {
      console.error("Error fetching readings:", error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [...new Set(readings.map(r => r.category))].sort();

  const filteredReadings = readings.filter(reading => {
    const matchesSearch = reading.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         reading.author.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "all" || reading.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center">
        <div className="animate-breathe">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
            <Book className="w-8 h-8 text-primary" strokeWidth={1.5} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-gradient" data-testid="reading-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-muted-foreground hover:text-foreground"
            data-testid="reading-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <Book className="w-5 h-5 text-primary" strokeWidth={1.5} />
            <span className="font-heading text-lg font-semibold">Reading</span>
          </div>
          <div className="w-24"></div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mb-3">
            Recommended Reading
          </h1>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            {readings.length} books to nourish your soul and support your journey
          </p>
        </div>

        {/* Today's Recommendation */}
        {dailyReading && (
          <Card className="glass-card rounded-3xl border-0 mb-10 overflow-hidden" data-testid="daily-reading-feature">
            <CardContent className="p-8">
              <span className="text-xs text-primary uppercase tracking-wide">Today's Recommendation</span>
              <h2 className="font-heading text-2xl font-semibold text-foreground mt-2 mb-1">
                {dailyReading.title}
              </h2>
              <p className="text-muted-foreground mb-4">by {dailyReading.author}</p>
              <p className="text-foreground leading-relaxed mb-4">
                {dailyReading.description}
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-block text-xs bg-primary/10 text-primary px-3 py-1 rounded-full">
                  {dailyReading.category}
                </span>
                {dailyReading.url && (
                  <a
                    href={dailyReading.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-md px-2 py-1"
                    aria-label={`Find ${dailyReading.title} by ${dailyReading.author} on Booktopia (opens in new tab)`}
                    data-testid="daily-reading-link"
                  >
                    <ExternalLink className="w-4 h-4" aria-hidden="true" />
                    <span>Find this book</span>
                  </a>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by title or author..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 rounded-xl bg-white/50"
              data-testid="reading-search"
            />
          </div>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 rounded-xl bg-white/50 border border-border text-foreground"
            data-testid="reading-category-filter"
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        {/* Readings List */}
        <div className="grid gap-4" role="list" aria-label="Book recommendations">
          {filteredReadings.map((reading, index) => (
            <Card
              key={reading.id}
              className="glass-card rounded-2xl border-0 card-hover"
              data-testid={`reading-${reading.id}`}
              role="listitem"
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="font-heading text-primary font-semibold" aria-hidden="true">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-lg font-semibold text-foreground">
                      {reading.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-2">
                      by {reading.author}
                    </p>
                    <p className="text-sm text-foreground/80 leading-relaxed mb-3">
                      {reading.description}
                    </p>
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="inline-block text-xs bg-muted px-2 py-1 rounded-full">
                        {reading.category}
                      </span>
                      {reading.url && (
                        <a
                          href={reading.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-md px-2 py-1"
                          aria-label={`Find ${reading.title} by ${reading.author} on Booktopia (opens in new tab)`}
                          data-testid={`reading-link-${reading.id}`}
                        >
                          <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
                          <span>Find book</span>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredReadings.length === 0 && (
          <div className="text-center py-12">
            <Book className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">No books found matching your search.</p>
          </div>
        )}

        {/* Closing Message */}
        <div className="text-center mt-12 py-8">
          <p className="font-handwritten text-2xl text-primary">
            "A book is a gift you can open again and again"
          </p>
        </div>
      </main>
    </div>
  );
};

export default ReadingPage;
